/**
 * Tough-Bar Builder - Navigation Module
 * Step navigation and lifecycle methods
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.navigation = {
  normalizeStepNumber(stepNumber) {
    const numericStep = Number(stepNumber);
    if (!Number.isFinite(numericStep)) return null;
    const normalized = Math.trunc(numericStep);
    if (normalized < 1) return null;
    return normalized;
  },

  recordStepSnapshotId(stepNumber, snapshotId) {
    const normalizedStep = this.normalizeStepNumber(stepNumber);
    const normalizedSnapshotId = String(snapshotId || '').trim();
    if (!normalizedStep || !normalizedSnapshotId) return;

    const nextMap = { ...(this.stepSnapshotIds || {}) };
    nextMap[normalizedStep] = normalizedSnapshotId;

    Object.keys(nextMap).forEach(key => {
      if (Number(key) > normalizedStep) {
        delete nextMap[key];
      }
    });

    this.stepSnapshotIds = nextMap;
  },

  getStepSnapshotId(stepNumber) {
    const normalizedStep = this.normalizeStepNumber(stepNumber);
    if (!normalizedStep) return null;
    const value = this.stepSnapshotIds?.[normalizedStep];
    if (!value) return null;
    const normalizedSnapshotId = String(value).trim();
    return normalizedSnapshotId || null;
  },

  clearStepSnapshotId(stepNumber) {
    const normalizedStep = this.normalizeStepNumber(stepNumber);
    if (!normalizedStep) return;

    const nextMap = { ...(this.stepSnapshotIds || {}) };
    if (!nextMap[normalizedStep]) return;

    delete nextMap[normalizedStep];
    this.stepSnapshotIds = nextMap;
  },

  canJumpToStep(stepNumber) {
    const targetStep = this.normalizeStepNumber(stepNumber);
    const currentStep = this.normalizeStepNumber(this.step || 1) || 1;
    if (!targetStep) return false;
    if (this.loading) return false;
    if (targetStep === 1) return currentStep > 1;
    return targetStep >= 2 && targetStep < currentStep;
  },

  canJumpToVisibleStep(stepNumber) {
    const targetStep = this.normalizeStepNumber(stepNumber);
    const currentVisibleStep = Math.min(this.normalizeStepNumber(this.step || 1) || 1, 3);
    if (!targetStep) return false;
    if (targetStep >= currentVisibleStep) return false;
    return this.canJumpToStep(targetStep);
  },

  isDesktopSideCartMode() {
    if (typeof this._isDesktopSideCartViewport === 'function') {
      return this._isDesktopSideCartViewport();
    }

    const desktopBreakpoint = typeof this.getDesktopSideCartBreakpoint === 'function'
      ? this.getDesktopSideCartBreakpoint()
      : 1855;

    return window.innerWidth >= desktopBreakpoint;
  },

  openFinalKitView() {
    const isDesktop = this.isDesktopSideCartMode();

    if (isDesktop) {
      if (typeof this.expandSideCart === 'function') {
        this.expandSideCart();
      }
      return;
    }

    if (typeof this.openSideCartDrawer === 'function') {
      this.openSideCartDrawer();
    }
  },

  async finalizeAccessoriesStep() {
    if (!this.canProceed) return;
    if (typeof this.setAccessoriesComplete === 'function') {
      this.setAccessoriesComplete(true);
    } else {
      this.context.accessories_complete = true;
    }

    this.openFinalKitView();

    if (this.step >= 2 && typeof this.syncSnapshotWithCurrentState === 'function') {
      try {
        await this.syncSnapshotWithCurrentState(true);
      } catch (snapshotError) {
        console.warn('[Tough-Bar Finalize] Snapshot refresh failed:', snapshotError);
      }
    }
  },

  trimContextToStep(targetStep) {
    const normalizedStep = this.normalizeStepNumber(targetStep) || 1;

    if (!this.context || typeof this.context !== 'object') {
      this.context = {};
    }

    // Step 3+ selections should not persist when user goes back to step 2 or 1.
    if (normalizedStep < 3) {
      delete this.context.accessories;
      delete this.context.accessories_complete;
      this.accessoryButtonsExpanded = false;
      this.selectedAccessories = [];
      if (typeof this.clearAccessoryCustomizeAvailability === 'function') {
        this.clearAccessoryCustomizeAvailability();
      }
      if (typeof this.prunePersistedCartStateToActiveItems === 'function') {
        this.prunePersistedCartStateToActiveItems();
      }
    }
  },

  syncInitialSideCartVisibilityForStep() {
    const isDesktop = this.isDesktopSideCartMode();
    if (!isDesktop) return;

    if (this.isFinalKitState) {
      if (typeof this.expandSideCart === 'function') {
        this.expandSideCart();
      }
      return;
    }

    if (typeof this.collapseSideCart === 'function') {
      this.collapseSideCart();
    }
  },

  async jumpToStep(stepNumber) {
    if (!this.canJumpToStep(stepNumber)) return;

    const targetStep = this.normalizeStepNumber(stepNumber);
    const previousStep = this.normalizeStepNumber(this.step || 1) || 1;
    if (!targetStep || targetStep >= previousStep) return;

    if (typeof this.dismissTransientUiState === 'function') {
      this.dismissTransientUiState();
    }

    if (targetStep === 1) {
      await this.resetBuilder();
      this.scrollViewportToTop();
      return;
    }

    this.cancelPendingSnapshotSync();

    const snapshotId = this.getStepSnapshotId(targetStep);
    if (snapshotId) {
      this.snapshotSuppressUrlUpdates = true;
      try {
        this.currentSharedConfigId = snapshotId;
        this.sharedConfigIdFromUrl = snapshotId;
        const loaded = await this.loadSnapshot(snapshotId);
        if (loaded) {
          this.trimContextToStep(targetStep);
          this.replaceHistoryState(snapshotId);
          this.recordStepSnapshotId(this.step, snapshotId);
          this.scrollViewportToTop();
          return;
        }
      } finally {
        this.snapshotSuppressUrlUpdates = false;
      }
    }

    await this.api({ go_back: true, target_step: targetStep });
    if (this.error) return;
    this.trimContextToStep(targetStep);
    this.scrollViewportToTop();

    if (this.step >= 2) {
      await this.syncSnapshotWithCurrentState(true);
    } else {
      this.currentSharedConfigId = null;
      this.sharedConfigIdFromUrl = null;
      this.clearSnapshotFromUrl(true);
    }
  },

  async init() {
    this.registerPopstateListener();
    const initializeCartMutationObserver = () => {
      if (typeof this.initCartMutationObserver === 'function') {
        this.initCartMutationObserver();
      }
    };

    const sharedConfigId = this.getSharedConfigIdFromUrl();
    this.sharedConfigIdFromUrl = sharedConfigId;

    if (sharedConfigId) {
      this.currentSharedConfigId = sharedConfigId;
      const loaded = await this.loadSnapshot(sharedConfigId);
      if (loaded) {
        this.syncInitialSideCartVisibilityForStep();
        this.recordStepSnapshotId(this.step, sharedConfigId);
        this.replaceHistoryState(sharedConfigId);
        initializeCartMutationObserver();
        return;
      }

      this.clearSnapshotFromUrl(true);
      this.currentSharedConfigId = null;
      this.sharedConfigIdFromUrl = null;
    }

    await this.api();
    this.syncInitialSideCartVisibilityForStep();
    this.replaceHistoryState(null);
    initializeCartMutationObserver();
  },

  getSharedConfigIdFromUrl() {
    try {
      const url = new URL(window.location.href);
      const value = url.searchParams.get('sharedConfigId');
      return value && value.trim() ? value.trim() : null;
    } catch (_error) {
      return null;
    }
  },

  registerPopstateListener() {
    if (this.popstateHandlerBound) return;

    this._handleToughBarPopstate = async event => {
      await this.handlePopState(event);
    };

    window.addEventListener('popstate', this._handleToughBarPopstate);
    this.popstateHandlerBound = true;
  },

  nextSnapshotSyncRequestId() {
    const next = Number(this._snapshotSyncRequestSeq || 0) + 1;
    this._snapshotSyncRequestSeq = next;
    return next;
  },

  isSnapshotSyncRequestActive(requestId) {
    return Number(requestId || 0) === Number(this._snapshotSyncRequestSeq || 0);
  },

  cancelPendingSnapshotSync() {
    this.nextSnapshotSyncRequestId();
  },

  async handlePopState(event) {
    if (this.loading) return;

    const stateSnapshotId = event?.state?.sharedConfigId || null;
    const urlSnapshotId = this.getSharedConfigIdFromUrl();
    const snapshotId = stateSnapshotId || urlSnapshotId;

    this.cancelPendingSnapshotSync();
    if (typeof this.dismissTransientUiState === 'function') {
      this.dismissTransientUiState();
    }
    this.snapshotSuppressUrlUpdates = true;
    try {
      if (snapshotId) {
        this.currentSharedConfigId = snapshotId;
        this.sharedConfigIdFromUrl = snapshotId;

        const loaded = await this.loadSnapshot(snapshotId);
        if (loaded) {
          this.syncInitialSideCartVisibilityForStep();
          this.recordStepSnapshotId(this.step, snapshotId);
          this.replaceHistoryState(snapshotId);
          return;
        }

        this.clearSnapshotFromUrl(true);
      }

      await this.resetBuilder({ skipHistory: true });
      this.currentSharedConfigId = null;
      this.sharedConfigIdFromUrl = null;
      this.replaceHistoryState(null);
    } finally {
      this.snapshotSuppressUrlUpdates = false;
    }
  },

  replaceHistoryState(sharedConfigId = null) {
    const state = {
      step: this.step || 1,
      sharedConfigId: sharedConfigId || null
    };
    window.history.replaceState(state, '', window.location.href);
  },

  updateUrlWithSnapshot(shortId, fullId = null, useReplace = false) {
    if (this.snapshotSuppressUrlUpdates) return;

    const shortSnapshotId = String(shortId || '').trim();
    const fullSnapshotId = String(fullId || '').trim();
    const urlSnapshotId = fullSnapshotId || shortSnapshotId;
    if (!urlSnapshotId) return;

    const url = new URL(window.location.href);
    const existingSnapshotId = url.searchParams.get('sharedConfigId');
    const normalizedFullId = fullSnapshotId || null;
    const currentState = window.history.state || {};
    if (existingSnapshotId === urlSnapshotId
      && currentState.sharedConfigId === urlSnapshotId
      && (currentState.fullSnapshotId || null) === normalizedFullId) {
      this.currentSharedConfigId = urlSnapshotId;
      this.sharedConfigIdFromUrl = urlSnapshotId;
      return;
    }

    url.searchParams.set('sharedConfigId', urlSnapshotId);

    this.currentSharedConfigId = urlSnapshotId;
    this.sharedConfigIdFromUrl = urlSnapshotId;
    this.recordStepSnapshotId(this.step, urlSnapshotId);

    const state = {
      step: this.step || 1,
      sharedConfigId: urlSnapshotId,
      shortSnapshotId: shortSnapshotId || null,
      fullSnapshotId: normalizedFullId
    };

    const method = (useReplace || existingSnapshotId === urlSnapshotId) ? 'replaceState' : 'pushState';
    window.history[method](state, '', url.toString());
  },

  clearSnapshotFromUrl(useReplace = true) {
    const url = new URL(window.location.href);
    const hadSnapshotParam = url.searchParams.has('sharedConfigId');
    url.searchParams.delete('sharedConfigId');
    if (!hadSnapshotParam) return;

    const state = {
      step: this.step || 1,
      sharedConfigId: null
    };

    const method = useReplace ? 'replaceState' : 'pushState';
    window.history[method](state, '', url.toString());
  },

  async syncSnapshotWithCurrentState(useReplace = false, options = {}) {
    const { throwOnError = false } = options;
    if (this.snapshotSuppressUrlUpdates) return null;
    if (this.step < 2) return null;
    const requestId = this.nextSnapshotSyncRequestId();

    try {
      const snapshotData = await this.createSnapshot();
      if (!snapshotData?.short_id) return null;
      if (!this.isSnapshotSyncRequestActive(requestId)) return null;
      if (this.snapshotSuppressUrlUpdates) return null;

      this.updateUrlWithSnapshot(snapshotData.short_id, snapshotData.snapshot_id, useReplace);
      return snapshotData;
    } catch (error) {
      if (!this.isSnapshotSyncRequestActive(requestId)) return null;
      if (throwOnError) throw error;
      console.warn('[Tough-Bar Snapshot] Failed to sync URL snapshot:', error);
      return null;
    }
  },

  async copyTextToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  },

  async shareConfiguration() {
    if (this.shareLoading || this.step < 3) return;

    this.shareLoading = true;
    this.shareCopied = false;
    this.error = null;

    try {
      const snapshotData = await this.syncSnapshotWithCurrentState(true, { throwOnError: true });
      if (!snapshotData?.short_id) {
        throw new Error('Failed to create share snapshot.');
      }

      await this.copyTextToClipboard(window.location.href);
      this.shareCopied = true;

      if (this._shareCopiedTimeout) {
        clearTimeout(this._shareCopiedTimeout);
      }

      this._shareCopiedTimeout = setTimeout(() => {
        this.shareCopied = false;
      }, 2000);
    } catch (error) {
      console.error('[Tough-Bar Share] Failed to copy share URL:', error);
      this.error = 'Could not create a shareable link right now.';
    } finally {
      this.shareLoading = false;
    }
  },

  isMobileViewport() {
    if (typeof window === 'undefined') {
      return false;
    }

    const viewportWidth = window.visualViewport?.width || window.innerWidth || document.documentElement?.clientWidth || 0;
    const isNarrowViewport = viewportWidth > 0 && viewportWidth <= 1200;
    const hasCoarsePointer = typeof window.matchMedia === 'function'
      ? window.matchMedia('(hover: none) and (pointer: coarse)').matches
      : false;
    const mobileUserAgent = typeof navigator !== 'undefined'
      ? /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
      : false;

    return isNarrowViewport || hasCoarsePointer || mobileUserAgent;
  },

  scrollViewportToTop() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    });
  },

  scrollBuilderToTopOnMobile() {
    this.scrollViewportToTop();
  },

  async nextStep() {
    if (!this.canProceed) return;
    const previousStep = this.step;

    await this.api({ proceed: true, current_step: previousStep });
    if (this.error) return;

    // Restoring a final-kit state through the old step contract should reveal the side cart.
    if (
      previousStep < 3
      && this.isFinalKitState
      && typeof this.expandSideCart === 'function'
      && this.isDesktopSideCartMode()
    ) {
      this.expandSideCart();
    }

    this.scrollViewportToTop();

    if (this.step >= 2) {
      const useReplace = this.step === previousStep;
      await this.syncSnapshotWithCurrentState(useReplace);
    } else {
      this.cancelPendingSnapshotSync();
      this.currentSharedConfigId = null;
      this.sharedConfigIdFromUrl = null;
      this.clearSnapshotFromUrl(true);
    }
  },

  async prevStep() {
    const currentStep = this.step;
    if (currentStep <= 1) return;
    const targetStep = currentStep - 1;

    if (typeof this.dismissTransientUiState === 'function') {
      this.dismissTransientUiState();
    }

    if (targetStep === 1) {
      await this.resetBuilder();
      this.scrollViewportToTop();
      return;
    }

    await this.api({ go_back: true, target_step: targetStep });
    if (this.error) return;
    this.trimContextToStep(targetStep);
    this.scrollViewportToTop();

    if (this.step >= 2) {
      const useReplace = this.step === currentStep;
      await this.syncSnapshotWithCurrentState(useReplace);
    } else {
      this.cancelPendingSnapshotSync();
      this.currentSharedConfigId = null;
      this.sharedConfigIdFromUrl = null;
      this.clearSnapshotFromUrl(true);
    }
  },

  async resetBuilder(options = {}) {
    const { skipHistory = false } = options;
    this.cancelPendingSnapshotSync();

    if (typeof this.dismissTransientUiState === 'function') {
      this.dismissTransientUiState();
    }

    this.context = {};
    this.localKit = {};
    this.response = { step: 1 };
    this.products = {};
    this.quantities = {};
    this.removedProducts = [];
    this.excludedProducts = [];
    this.failedImages = [];
    this.pricingExpanded = false;
    this.toughBarExpanded = true;
    this.deviceExpanded = true;
    this.accessoriesExpanded = true;
    this.accessoryButtonsExpanded = false;
    this.accessoryButtonsCache = [];
    this.error = null;
    this.clearAccessories();
    this.resetTabletContext();
    this.modal = { open: false, type: null };
    this.tabletSelectionSignature = null;
    this.tabletSelectionConfirmed = null;
    this.currentSharedConfigId = null;
    this.sharedConfigIdFromUrl = null;
    this.stepSnapshotIds = {};
    this.shareLoading = false;
    this.shareCopied = false;
    this.sideCartCollapsed = true;

    if (this._shareCopiedTimeout) {
      clearTimeout(this._shareCopiedTimeout);
      this._shareCopiedTimeout = null;
    }

    if (this._cartStateSnapshotRefreshTimer) {
      clearTimeout(this._cartStateSnapshotRefreshTimer);
      this._cartStateSnapshotRefreshTimer = null;
    }
    this._cartStateSnapshotRefreshInFlight = false;
    this._cartStateSnapshotRefreshPending = false;

    await this.$nextTick();
    await this.api();

    if (!skipHistory) {
      this.clearSnapshotFromUrl(true);
    }
  },
};
